<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><i class="fa fa-balance-scale"></i> {{ $t('arTitle') }}</h1>
        <p class="text-muted mb-0">{{ $t('arSubtitle') }}</p>
      </div>
      <router-link to="/authority-request/new" class="btn btn-success"><i class="fa fa-plus"></i> {{ $t('arNew') }}</router-link>
    </div>

    <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>
    <div class="row">
      <div class="col-12">
        <b-tabs v-model="tabIndex" @input="onTab">
          <b-tab :title="$t('arTabOpen')"></b-tab>
          <b-tab :title="$t('arTabAwaiting') + (counters.awaiting ? ' (' + counters.awaiting + ')' : '')"></b-tab>
          <b-tab :title="$t('arTabAnswered')"></b-tab>
          <b-tab :title="$t('all')"></b-tab>
          <b-tab :title="$t('arTabPools')"></b-tab>
        </b-tabs>
        <div class="tile" v-if="tabIndex !== 4">
          <div class="form-inline">
            <b-form-select v-model="filter.source" class="mr-1 mb-1" :options="sourceOptions" @change="load"></b-form-select>
            <b-form-select v-model="filter.requestType" class="mr-1 mb-1" :options="typeOptions" @change="load"></b-form-select>
            <b-form-select v-model="filter.sourceNumber" class="mr-1 mb-1" :options="poolOptions" @change="load"></b-form-select>
            <b-form-input v-model="filter.keyword" class="mr-1 mb-1" style="min-width: 240px" :placeholder="$t('arKeywordPh')" @keydown.enter="load"></b-form-input>
            <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
          </div>
        </div>
      </div>

      <!-- Số pool -->
      <div class="col-12" v-if="tabIndex === 4">
        <div class="tile">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h5 class="mb-0">{{ $t('arTabPools') }} <span class="small text-muted">— {{ $t('arPoolHint') }}</span></h5>
            <b-button v-if="canEditPool" size="sm" variant="success" @click="editPool(null)"><i class="fa fa-plus"></i> {{ $t('arPoolAdd') }}</b-button>
          </div>
          <div v-if="poolError" class="alert alert-danger py-2 small">{{ poolError }}</div>
          <div class="table-responsive">
            <table class="table table-sm table-hover mb-0">
              <thead><tr><th>{{ $t('arPoolNumber') }}</th><th>{{ $t('arPoolCarrier') }}</th><th>Gateway</th><th>{{ $t('arPoolContact') }}</th><th>{{ $t('arPoolNote') }}</th><th>{{ $t('arPoolMentioned') }}</th><th>{{ $t('arStatus') }}</th><th></th></tr></thead>
              <tbody>
                <tr v-for="p in poolRows" :key="p._id">
                  <td><b>{{ p.number }}</b></td><td>{{ p.carrier }}</td><td class="small">{{ p.gateway && p.gateway.name }}</td><td class="small">{{ p.contact }}</td><td class="small text-muted">{{ p.note }}</td>
                  <td><span class="badge" :class="p.mentioned30d ? 'badge-warning' : 'badge-light border'">{{ p.mentioned30d }} / 30 {{ $t('arDays') }}</span></td>
                  <td><span class="badge" :class="p.active ? 'badge-success' : 'badge-secondary'">{{ p.active ? $t('arActive') : $t('arInactive') }}</span></td>
                  <td><b-button v-if="canEditPool" size="sm" variant="link" @click="editPool(p)"><i class="fa fa-edit"></i></b-button></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="poolForm" class="border rounded p-3 mt-3" style="background:#fafbfc">
            <div class="form-row">
              <div class="col-md-2"><b-form-group :label="$t('arPoolNumber')"><b-form-input v-model="poolForm.number" size="sm" placeholder="510117"></b-form-input></b-form-group></div>
              <div class="col-md-2"><b-form-group :label="$t('arPoolCarrier')"><b-form-input v-model="poolForm.carrier" size="sm" placeholder="Leeon"></b-form-input></b-form-group></div>
              <div class="col-md-3"><b-form-group label="Gateway"><b-form-select v-model="poolForm.gateway" size="sm" :options="gatewayOptions"></b-form-select></b-form-group></div>
              <div class="col-md-2"><b-form-group :label="$t('arPoolContact')"><b-form-input v-model="poolForm.contact" size="sm"></b-form-input></b-form-group></div>
              <div class="col-md-2"><b-form-group :label="$t('arPoolNote')"><b-form-input v-model="poolForm.note" size="sm"></b-form-input></b-form-group></div>
              <div class="col-md-1"><b-form-group :label="$t('status')"><b-form-checkbox v-model="poolForm.active" switch>{{ $t('arActive') }}</b-form-checkbox></b-form-group></div>
            </div>
            <b-button size="sm" variant="primary" class="mr-1" @click="savePool">{{ $t('save') }}</b-button>
            <b-button size="sm" variant="secondary" @click="poolForm = null">{{ $t('cancel') }}</b-button>
          </div>
        </div>
      </div>

      <div class="col-12" v-if="tabIndex !== 4">
        <div class="tile py-2">
          <span class="mr-4"><b class="h5 mb-0">{{ counters.overdue }}</b> <span class="text-muted small">{{ $t('arOverdue') }}</span></span>
          <span class="mr-4"><b class="h5 mb-0">{{ counters.awaiting }}</b> <span class="text-muted small">{{ $t('arAwaitingExport') }}</span></span>
          <span class="mr-4"><b class="h5 mb-0">{{ counters.repeatClients }}</b> <span class="text-muted small">{{ $t('arRepeatClients') }}</span></span>
          <span v-if="counters.topPool"><b class="h5 mb-0">{{ counters.topPool.number }}</b> <span class="text-muted small">{{ $t('arPoolMentionedN', [counters.topPool.n]) }}</span></span>
        </div>
      </div>

      <div class="col-12" v-if="tabIndex !== 4">
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="total">{{ $t('found', [total]) }}</div>
          <div class="c-paging__right">
            <b-pagination v-if="total > filter.pageSize" class="pull-right" size="sm" v-model="filter.page" :total-rows="total" :per-page="filter.pageSize" @input="load"></b-pagination>
          </div>
        </div>
      </div>

      <div class="col-12" v-if="items.length && tabIndex !== 4">
        <div class="tile">
          <div class="table-responsive">
            <table class="table table-hover table-sm mb-0">
              <thead><tr>
                <th>{{ $t('arCode') }}</th><th>{{ $t('arSource') }}</th><th>{{ $t('arType') }}</th><th>{{ $t('arInput') }}</th>
                <th>{{ $t('arResult') }}</th><th>{{ $t('arDue') }}</th><th>{{ $t('arStatus') }}</th><th></th>
              </tr></thead>
              <tbody>
                <tr v-for="it in items" :key="it._id">
                  <td><b>{{ it.code }}</b><div class="small text-muted">{{ it.reference }}</div></td>
                  <td>{{ sourceLabel(it.source) }}<span v-if="it.sourceName"> · {{ it.sourceName }}</span></td>
                  <td class="small">{{ typeLabel(it.requestType) }}</td>
                  <td class="small">
                    <template v-if="it.requestType === 'recordingsByNumber'">{{ it.scope && it.scope.number }} · {{ it.scope && it.scope.client && it.scope.client.domain }}</template>
                    <template v-else>{{ it.sourceNumber }} → {{ it.items.length }} {{ $t('arLines') }}</template>
                  </td>
                  <td>
                    <span v-if="count(it,'one')" class="badge badge-success mr-1">{{ count(it,'one') }} {{ $t('arFound') }}</span>
                    <span v-if="count(it,'many')" class="badge badge-warning mr-1">{{ count(it,'many') }} {{ $t('arMany') }}</span>
                    <span v-if="count(it,'none')" class="badge badge-secondary mr-1">{{ count(it,'none') }} {{ $t('arNone') }}</span>
                    <span v-if="count(it,'pending')" class="badge badge-light border mr-1">{{ count(it,'pending') }} {{ $t('arPending') }}</span>
                    <span v-if="count(it,'error')" class="badge badge-danger mr-1">{{ count(it,'error') }} {{ $t('arErrorCount') }}</span>
                  </td>
                  <td class="small" :class="{'text-danger': it.dueTime && it.dueTime < now && it.status === 'open'}">{{ it.dueTime | moment('DD/MM HH:mm') }}</td>
                  <td>
                    <span v-if="it.status === 'answered'" class="badge badge-success">{{ $t('arAnswered') }}</span>
                    <span v-else-if="hasExport(it,'awaitingApproval')" class="badge badge-warning">{{ $t('arAwaitingApproval') }}</span>
                    <span v-else-if="hasExport(it,'processing') || hasExport(it,'pending') || hasExport(it,'waitingRestore')" class="badge badge-info">{{ $t('arExporting') }}</span>
                    <span v-else class="badge badge-primary">{{ $t('arOpen') }}</span>
                  </td>
                  <td><router-link :to="'/authority-request/' + it._id" class="btn btn-sm btn-outline-primary">{{ $t('view') }}</router-link></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div class="col-12 text-center text-muted" v-else-if="loaded && tabIndex !== 4">{{ $t('arEmpty') }}</div>
    </div>
  </main>
</template>

<script>
import arI18n from './authority-request-i18n';

const TABS = ['open', 'awaitingApproval', 'answered', 'all', 'pools'];

export default {
  i18n: arI18n,
  data() {
    return {
      tabIndex: 0, items: [], total: 0, loaded: false, pools: [], now: Date.now(), error: '',
      counters: {overdue: 0, awaiting: 0, repeatClients: 0},
      filter: {source: null, requestType: null, sourceNumber: null, keyword: '', page: 1, pageSize: 20},
      poolRows: [], poolForm: null, poolError: '', gateways: [],
    };
  },
  computed: {
    canEditPool() { const u = this.$store.state.user || {}; return ['owner', 'admin', 'tech'].includes(u.role); },
    gatewayOptions() { return [{value: null, text: '—'}, ...this.gateways.map(g => ({value: g._id, text: g.name}))]; },
    sourceOptions() { return [{value: null, text: this.$t('arSource') + ': ' + this.$t('all')}, ...['carrier', 'attt', 'police', 'customer', 'other'].map(v => ({value: v, text: this.sourceLabel(v)}))]; },
    typeOptions() { return [{value: null, text: this.$t('arType') + ': ' + this.$t('all')}, ...['identify', 'recordingsByCdr', 'recordingsByNumber', 'bulkIdentify', 'other'].map(v => ({value: v, text: this.typeLabel(v)}))]; },
    poolOptions() { return [{value: null, text: this.$t('arPool') + ': ' + this.$t('all')}, ...this.pools.map(p => ({value: p.number, text: p.number + (p.carrier ? ' · ' + p.carrier : '')}))]; },
  },
  created() {
    this.$service.get('/authority-request/pools').then(r => { this.pools = r.data || []; }).catch(() => {});
    this.load();
  },
  methods: {
    sourceLabel(v) { return this.$t('arSource_' + (v || 'other')); },
    typeLabel(v) { return this.$t('arType_' + (v || 'other')); },
    count(it, st) { return (it.items || []).filter(i => i.matchStatus === st).length; },
    hasExport(it, st) { return (it.exports || []).some(e => e.status === st); },
    onTab() { if (this.tabIndex === 4) { this.loadPools(); return; } this.filter.page = 1; this.load(); },
    async loadPools() {
      this.poolError = '';
      try { const r = await this.$service.get('/caller-pool'); this.poolRows = r.data || []; } catch (e) { this.poolRows = []; }
      if (!this.gateways.length) this.$service.get('/gateway', {params: {page: 1, pageSize: 200}}).then(r => { this.gateways = (r.data && r.data.docs) || r.data || []; }).catch(() => {});
    },
    editPool(p) { this.poolError = ''; this.poolForm = p ? {_id: p._id, number: p.number, carrier: p.carrier, gateway: p.gateway ? p.gateway._id : null, contact: p.contact, note: p.note, active: p.active} : {number: '', carrier: '', gateway: null, contact: '', note: '', active: true}; },
    async savePool() {
      this.poolError = '';
      try {
        if (this.poolForm._id) await this.$service.put('/caller-pool/' + this.poolForm._id, this.poolForm);
        else await this.$service.post('/caller-pool', this.poolForm);
        this.poolForm = null; await this.loadPools();
        this.$service.get('/authority-request/pools').then(r => { this.pools = r.data || []; }).catch(() => {});
      } catch (e) { this.poolError = (e.response && e.response.data && e.response.data.message) || this.$t('arError'); }
    },
    reset() { this.filter = {source: null, requestType: null, sourceNumber: null, keyword: '', page: 1, pageSize: 20}; this.load(); },
    async load() {
      try {
        const res = await this.$service.get('/authority-request', {params: {...this.filter, tab: TABS[this.tabIndex]}});
        this.items = res.data.docs || []; this.total = res.data.totalDocs || 0;
        this.counters = res.data.counters || this.counters;
      } catch (e) { this.items = []; this.error = (e.response && e.response.data && e.response.data.message) || this.$t('arError'); }
      this.loaded = true; this.now = Date.now();
    },
  },
};
</script>
